import moment from "moment";
import { formatAppointmentDateTime, formattedDate } from "../utils/dateUtils";
import api from "./Api";

export interface ApiResponse<T> {
  result: T;
  isSuccess: boolean;
  message: string;
  statusCode: number;
}

const invoice = process.env.REACT_APP_CASE_URL || "";
if (!invoice) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_CASE_URL in your environment variables.",
  );
}

const InvoiceAPI = api(invoice);

const getStandardChargesList = async (caseInfoId: any): Promise<any[]> => {
  try {
    const response = await InvoiceAPI.get<any>(
      `/api/invoice/stdcharges/by-case?caseInfoId=${caseInfoId}`,
    );
    const list = response.data?.result.map((item: any) => ({
      value: item.billingConfigStdChgId,
      label: item.standardChargeNote,
      chargesRate: item.standardChargeRate,
      currencySymbol: item.currencySymbol,
    }));
    return list;
  } catch (error) {
    console.error("Error getting Data:", error);
    throw new Error(
      "Failed to get the charges data. Please check the data and try again.",
    );
  }
};

const getStandardChargesByCaseID = async (params: any): Promise<any[]> => {
  try {
    const response = await InvoiceAPI.post<any>(
      `/api/invoice/stdcharges/list`,
      params,
    );

    return response.data?.result || [];
  } catch (error) {
    console.error("Error getting Data:", error);
    throw error;
  }
};

const addStandardChargesLineItems = async (params: any): Promise<any> => {
  try {
    const response = await InvoiceAPI.post(
      "/api/invoice/stdcharges/add",
      params,
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding standard charges:", error);
    throw new Error("Unable to add the data. Please try again later.");
  }
};

const updateStandardChargesLineItems = async (params: any): Promise<any> => {
  try {
    const response = await InvoiceAPI.put(
      "/api/invoice/stdcharges/update",
      params,
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding standard charges:", error);
    throw new Error("Unable to add the data. Please try again later.");
  }
};
const deleteStandardChargesLineItems = async (
  invoiceLineItemStdChgId: any,
): Promise<any> => {
  try {
    const response = await InvoiceAPI.get(
      `/api/invoice/stdcharges/delete?invoiceLineItemStdChgId=${invoiceLineItemStdChgId}`,
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error deleting standard charges:", error);
    throw new Error("Unable to delete the row. Please try again later.");
  }
};

const getLineItemListByCaseID = async (params: any): Promise<any[]> => {
  try {
    const response = await InvoiceAPI.post<any>(
      `/api/invoice/line-items/list/by-case`,
      params,
    );

    return response.data?.result || [];
  } catch (error) {
    console.error("Error getting Data:", error);
    throw error;
  }
};

const addLineItems = async (params: any): Promise<any> => {
  try {
    const response = await InvoiceAPI.post(
      "/api/invoice/line-item/custom/add",
      params,
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding standard charges:", error);
    throw new Error("Unable to add the data. Please try again later.");
  }
};

const getInvoiceSummaryByCase = async (caseInfoId: any): Promise<any> => {
  try {
    const response = await InvoiceAPI.get(
      `/api/invoice/summary/by-case?caseInfoId=${caseInfoId}`,
    );
    const data = response.data?.result;

    return data;
  } catch (error) {
    console.error("Error getting invoice summary:", error);
    throw new Error("Unable to get the data. Please try again later.");
  }
};

const updateAutomatedLineItems = async (params: any): Promise<any> => {
  try {
    const response = await InvoiceAPI.put(
      "/api/invoice/line-item/automated/update",
      params,
    );
    const { isSuccess, statusCode, message, result } = response.data;

    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding standard charges:", error);
    throw new Error("Unable to add the data. Please try again later.");
  }
};

const updateCustomLineItems = async (params: any): Promise<any> => {
  try {
    const response = await InvoiceAPI.put(
      "/api/invoice/line-item/custom/update",
      params,
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding standard charges:", error);
    throw new Error("Unable to add the data. Please try again later.");
  }
};

const deleteLineItems = async (params: any): Promise<any> => {
  try {
    const response = await InvoiceAPI.delete(
      "/api/invoice/line-item/custom/delete",
      params,
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding standard charges:", error);
    throw new Error("Unable to add the data. Please try again later.");
  }
};

const getInvoiceChangeLog = async (caseInfoId: any): Promise<any> => {
  try {
    const response = await InvoiceAPI.get(
      `/api/invoice/changelog?caseInfoId=${caseInfoId}`,
    );
    const result: any = response.data?.result ?? [];
    const filterData = result?.map((item: any) => ({
      ...item,
      dateOfAppointmentFormat:
        formatAppointmentDateTime(item?.dateOfAppointment) || null,
      auditChangedOn: formatAppointmentDateTime(item?.auditChangedOn) || null,
    }));
    return filterData;
  } catch (error) {
    console.error("Error getting invoice change log:", error);
    throw new Error("Unable to get the data. Please try again later.");
  }
};

const getInvoiceUserComments = async (
  invoiceLineitemStdChgId: any,
): Promise<any> => {
  try {
    const response = await InvoiceAPI.get(
      `/api/invoice/user-comments?invoiceLineitemStdChgId=${invoiceLineitemStdChgId}`,
    );
    const data = response.data?.result;

    return data;
  } catch (error) {
    console.error("Error getting invoice comments:", error);
    throw new Error("Unable to get the data. Please try again later.");
  }
};

const addInvoiceUserComments = async (params: any): Promise<any> => {
  try {
    const response = await InvoiceAPI.post(
      "/api/invoice/user-comments/add",
      params,
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding invoice comments:", error);
    throw new Error("Unable to add the data. Please try again later.");
  }
};

const updateInvoiceUserComments = async (params: any): Promise<any> => {
  try {
    const response = await InvoiceAPI.put(
      "/api/invoice/user-comments/update",
      params,
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding invoice comments:", error);
    throw new Error("Unable to add the data. Please try again later.");
  }
};

export {
  getStandardChargesList,
  getStandardChargesByCaseID,
  addStandardChargesLineItems,
  updateStandardChargesLineItems,
  deleteStandardChargesLineItems,
  getLineItemListByCaseID,
  addLineItems,
  updateCustomLineItems,
  updateAutomatedLineItems,
  deleteLineItems,
  getInvoiceSummaryByCase,
  getInvoiceChangeLog,
  getInvoiceUserComments,
  addInvoiceUserComments,
  updateInvoiceUserComments,
};
